import { formatDistanceToNow, differenceInDays } from "date-fns";
import { useMemo, useCallback } from "react";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { isSnoozed } from "@/lib/pendingLogic";
import { calculatePeriodStart } from "@/lib/subscriptionDateUtils";
import { isUnscheduledDatabaseSubscription } from "@/lib/utils/subscriptionUtils";
import { type PaymentHistory } from "@/repositories/interfaces";
import { BillingCycleMapper } from "@/repositories/supabase/utils/BillingCycleMapper";
import {
  type NotificationItem,
  type NotificationType,
} from "@/types/notification";

/**
 * Check if subscription has a paid payment in the current billing period
 */
const hasPaidPaymentInCurrentPeriod = (
  subscription: DatabaseSubscription,
  payments: PaymentHistory[],
): boolean => {
  if (!subscription.next_payment_date || !subscription.billing_cycle) {
    return false;
  }

  const nextPaymentDate = new Date(subscription.next_payment_date);

  // Map database billing_cycle string to frequency and customDays
  const { frequency, customDays } =
    BillingCycleMapper.mapBillingCycleToFrequency(subscription.billing_cycle);

  const periodStart = calculatePeriodStart(
    nextPaymentDate,
    frequency,
    customDays,
  );

  // Filter payments for this subscription
  const subscriptionPayments = payments.filter(
    (p) => p.subscription_id === subscription.id,
  );

  // Check if any payment in current period is marked as paid
  return subscriptionPayments.some((payment) => {
    const paymentDate = new Date(payment.payment_date);
    return (
      payment.status === "paid" &&
      paymentDate >= periodStart &&
      paymentDate <= nextPaymentDate
    );
  });
};

/**
 * Transform subscription data into actionable notification items
 * Sorts by severity (urgent → warning → info) and date
 * Optimized with memoization for performance
 * Respects user notification preferences
 */
export const useNotificationItems = (
  subscriptions: DatabaseSubscription[],
  paymentHistory: PaymentHistory[],
): NotificationItem[] => {
  const { preferences } = useNotificationPreferences();
  const { canUseRenewalAlerts } = useFeatureGate();

  // Memoize notification building logic
  const buildNotifications = useCallback(
    (subs: DatabaseSubscription[], payments: PaymentHistory[]) => {
      // Filter out snoozed subscriptions - they won't generate notifications
      // until the snooze period expires or is manually cleared
      const activeSubscriptions = subs.filter((sub) => !isSnoozed(sub));

      // Default thresholds if preferences not loaded yet
      const thresholds = {
        dueSoon: preferences?.days_before_due ?? 3,
        renewal: preferences?.days_before_renewal ?? 7,
        trialEnd: preferences?.days_before_trial_end ?? 3,
      };

      const notificationToggles = {
        notifyDueSoon: preferences?.notify_due_soon ?? true,
        notifyOverdue: preferences?.notify_overdue ?? true,
        notifyRenewal:
          canUseRenewalAlerts && (preferences?.notify_auto_renewal ?? true),
        notifyTrial: preferences?.notify_trial_ending ?? true,
      };
      const notifications: NotificationItem[] = [];
      const today = new Date();

      activeSubscriptions.forEach((sub) => {
        // Payment Overdue / Due Soon
        if (sub.next_payment_date && sub.status === "active") {
          // Skip PAYG subscriptions - they don't have fixed payment schedules
          if (isUnscheduledDatabaseSubscription(sub)) {
            return; // Skip to next subscription
          }

          // Skip if current period already has a paid payment
          if (hasPaidPaymentInCurrentPeriod(sub, payments)) {
            return; // Skip to next subscription
          }

          const nextPaymentDate = new Date(sub.next_payment_date);
          const daysUntil = differenceInDays(nextPaymentDate, today);

          if (daysUntil < 0) {
            if (notificationToggles.notifyOverdue) {
              notifications.push({
                id: `overdue-${sub.id}`,
                type: "payment_overdue" as NotificationType,
                subscriptionId: sub.id,
                subscriptionName: sub.name,
                title: `${sub.name} is overdue`,
                message: `Payment was due ${formatDistanceToNow(nextPaymentDate, { addSuffix: true })}`,
                daysUntil: Math.abs(daysUntil),
                date: nextPaymentDate,
                severity: "urgent",
                actionLabel: "Mark as Paid",
                actionPath: `/?subscription=${sub.id}`,
                amount: sub.amount,
                currency: sub.currency,
              });
            }
          } else if (daysUntil <= thresholds.dueSoon) {
            // Payment Due Soon (within threshold days)
            if (notificationToggles.notifyDueSoon) {
              notifications.push({
                id: `due-soon-${sub.id}`,
                type: "payment_due_soon" as NotificationType,
                subscriptionId: sub.id,
                subscriptionName: sub.name,
                title: `${sub.name} due soon`,
                message:
                  daysUntil === 0
                    ? "Payment is due today"
                    : `Payment due in ${daysUntil} ${daysUntil === 1 ? "day" : "days"}`,
                daysUntil,
                date: nextPaymentDate,
                severity: daysUntil === 0 ? "urgent" : "warning",
                actionLabel: "View Details",
                actionPath: `/?subscription=${sub.id}`,
                amount: sub.amount,
                currency: sub.currency,
              });
            }
          }
        }

        // Auto-Renewal Alert (within 7 days)
        if (sub.auto_renewal_date && sub.status === "active") {
          const autoRenewalDate = new Date(sub.auto_renewal_date);
          const daysUntil = differenceInDays(autoRenewalDate, today);

          if (daysUntil >= 0 && daysUntil <= thresholds.renewal) {
            if (notificationToggles.notifyRenewal) {
              notifications.push({
                id: `renewal-${sub.id}`,
                type: "auto_renewal_alert" as NotificationType,
                subscriptionId: sub.id,
                subscriptionName: sub.name,
                title: `${sub.name} renews soon`,
                message:
                  daysUntil === 0
                    ? "Auto-renewal is today"
                    : `Auto-renewal in ${daysUntil} ${daysUntil === 1 ? "day" : "days"}`,
                daysUntil,
                date: autoRenewalDate,
                severity: "warning",
                actionLabel: "Review Subscription",
                actionPath: `/?subscription=${sub.id}`,
              });
            }
          }
        }

        // Trial Ending (within 3 days)
        if (sub.trial_end_date && sub.status === "trial") {
          const trialEndDate = new Date(sub.trial_end_date);
          const daysUntil = differenceInDays(trialEndDate, today);

          if (daysUntil >= 0 && daysUntil <= thresholds.trialEnd) {
            if (notificationToggles.notifyTrial) {
              notifications.push({
                id: `trial-${sub.id}`,
                type: "trial_ending" as NotificationType,
                subscriptionId: sub.id,
                subscriptionName: sub.name,
                title: `${sub.name} trial ending`,
                message:
                  daysUntil === 0
                    ? "Trial ends today"
                    : `Trial ends in ${daysUntil} ${daysUntil === 1 ? "day" : "days"}`,
                daysUntil,
                date: trialEndDate,
                severity: "warning",
                actionLabel: "Manage Trial",
                actionPath: `/?subscription=${sub.id}`,
              });
            }
          }
        }
      });

      // Sort by severity (urgent first) then by days until event
      return notifications.sort((a, b) => {
        // Severity priority: urgent > warning > info
        const severityOrder = { urgent: 0, warning: 1, info: 2 };
        const severityDiff =
          severityOrder[a.severity] - severityOrder[b.severity];

        if (severityDiff !== 0) return severityDiff;

        // Within same severity, sort by days until (soonest first)
        return (a.daysUntil ?? 0) - (b.daysUntil ?? 0);
      });
    },
    [canUseRenewalAlerts, preferences],
  );

  return useMemo(
    () => buildNotifications(subscriptions, paymentHistory),
    [subscriptions, paymentHistory, buildNotifications],
  );
};
